import type { PriceBreakdown } from './checkout';

/**
 * Where the wallet-derived tax came from:
 *   - `CALCULATE` — the real `/calculate-estimated-tax` endpoint (US/CA wallet
 *                   that matches the session country). Authoritative for the
 *                   buyer's actual jurisdiction; safe to flush over any
 *                   previously-held checkout tax at wallet cancel/failure.
 *   - `DEFAULT`   — the `/handle-default-tax` fallback (non-US/CA wallet OR
 *                   cross-jurisdiction wallet — see routing in
 *                   `useWalletZipTaxCalculator`). NOT authoritative for the
 *                   buyer's jurisdiction; must NOT overwrite an existing valid
 *                   tax breakdown at wallet cancel/failure (see
 *                   `applyWalletTaxToCheckout`).
 */
export enum WalletZipTaxSource {
  CALCULATE = 'calculate',
  DEFAULT = 'default',
}

export interface WalletZipTaxResult {
  zipCode: string;
  state?: string;
  priceBreakdown: PriceBreakdown;
  source: WalletZipTaxSource;
}

/**
 * Discriminant for `WalletZipTaxOutcome`.
 *
 * - `SKIPPED`   — session/FF gate closed, OR the wallet gave no ZIP AND
 *                 the checkout already holds a valid pre-open tax (the
 *                 pre-open tax is authoritative; calling
 *                 `/handle-default-tax` would only dirty the BE session
 *                 and risk an undercharge if the buyer authorizes against
 *                 the default). An empty wallet ZIP with NO pre-open
 *                 valid tax still routes to `/handle-default-tax` so the
 *                 sheet gets a breakdown. No BE call was made.
 * - `SAME_ZIP`  — the wallet's ZIP (post-normalization) equals the ZIP already
 *                 held in checkout state (`addressDetails.zipCode`, falling back
 *                 to `customer.address.zipCode`). The BE tax we already have is
 *                 correct for this ZIP, so no BE call is made and callers should
 *                 resolve/return the sheet immediately with the current total.
 *                 `capturedWalletTaxRef` is deliberately left untouched — checkout state
 *                 already reflects this ZIP, so there is nothing new to forward
 *                 at finalize.
 * - `INVALID`   — BE call ran and returned `isValid: false`.
 * - `ERROR`     — BE call threw; caller decides whether to log.
 * - `UNCHANGED` — BE returned a valid breakdown whose `priceTotal` equals the
 *                 checkout's current `pricing.priceBreakdown.priceTotal`, so
 *                 the sheet's displayed total does NOT need to move. Callers
 *                 should skip any sheet-total update path (e.g. Stripe's
 *                 `elements.update({ amount })`) since it'd be a no-op. Still
 *                 stored in `capturedWalletTaxRef` so `applyWalletTaxToCheckout` can
 *                 forward any updated `state` / `zipCode` at finalize.
 * - `SUCCESS`   — BE returned a valid breakdown WITH a different `priceTotal`;
 *                 also stored in `capturedWalletTaxRef`.
 */
export enum WalletZipTaxStatus {
  SKIPPED = 'skipped',
  SAME_ZIP = 'same_zip',
  INVALID = 'invalid',
  ERROR = 'error',
  UNCHANGED = 'unchanged',
  SUCCESS = 'success',
}

/** Fields attached to every `Wallet ZIP tax outcome` RUM log. */
export interface WalletZipTaxLogFields {
  hasWalletZip: boolean;
  walletCountry: string;
  sessionCountry: string;
  checkoutTaxValid: boolean;
  hasCheckoutZip: boolean;
}

export type WalletZipTaxOutcome =
  | { status: WalletZipTaxStatus.SKIPPED }
  | { status: WalletZipTaxStatus.SAME_ZIP }
  | { status: WalletZipTaxStatus.INVALID }
  | { status: WalletZipTaxStatus.ERROR; error: unknown }
  | { status: WalletZipTaxStatus.UNCHANGED; result: WalletZipTaxResult }
  | { status: WalletZipTaxStatus.SUCCESS; result: WalletZipTaxResult };
